import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DEFAULT_PAGE_LIMIT,
  ENDPOINTS,
  type OutboundAttentionStatus,
  type OutboundDay,
  type OutboundHealthCell,
  type OutboundQueueItem,
} from '@clm/shared-types';

import { fetchOutboundHealth } from '../../api/quality';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PanelProblem, Tile } from './panel';
import { FORBIDDEN_COPY, usePaging, usePanel } from './panel-state';

/**
 * (b) Outbound health — NFR-11 "Abandoned is surfaced, never dropped"; NFR-15.
 *
 * Three tables from three statements. The queue-depth cells are the ratified
 * `vw_outbound_health` and are **not** filtered by scope — a queue depth is a
 * fact about the system, not about a viewer. The settled-per-day series and the
 * Failed/Abandoned row list read `vw_outbound_queue_scope` under the scope
 * predicate (G20), so two viewers may see different rows under the same cells.
 * The panel says so under its heading (FR-27).
 *
 * No chart library: the per-day bar is a CSS width from two wire numbers, marked
 * `aria-hidden`, and the number itself is always printed as text.
 */

const PANEL = 'Outbound health';
const ENDPOINT_PATH = ENDPOINTS['quality-outbound-health'].path;

type StatusChoice = 'both' | OutboundAttentionStatus;

const CELL_COLUMNS: readonly DataTableColumn<OutboundHealthCell>[] = [
  { id: 'target', header: 'Target', cell: (c) => c.target_system },
  { id: 'status', header: 'Status', cell: (c) => c.queue_status },
  { id: 'items', header: 'Items', cell: (c) => c.item_count, cellClassName: 'num' },
  { id: 'oldest', header: 'Oldest', cell: (c) => c.oldest_utc ?? '—' },
  { id: 'attempts', header: 'Max attempts', cell: (c) => c.max_attempts, cellClassName: 'num' },
  { id: 'retry', header: 'Earliest retry', cell: (c) => c.earliest_next_retry_utc ?? '—' },
];

function dayColumns(pageMax: number): readonly DataTableColumn<OutboundDay>[] {
  return [
    { id: 'day', header: 'Day', cell: (d) => d.day },
    { id: 'target', header: 'Target', cell: (d) => d.target_system },
    { id: 'sent', header: 'Sent', cell: (d) => d.sent, cellClassName: 'num' },
    { id: 'failed', header: 'Failed', cell: (d) => d.failed, cellClassName: 'num' },
    { id: 'abandoned', header: 'Abandoned', cell: (d) => d.abandoned, cellClassName: 'num' },
    {
      id: 'bar',
      header: 'Bar',
      cell: (d) => {
        const settled = d.sent + d.failed + d.abandoned;
        return (
          <span className="qbar-wrap">
            <span
              className="qbar"
              aria-hidden="true"
              data-testid="qbar"
              style={{ ['--w' as string]: pageMax === 0 ? 0 : settled / pageMax }}
            />
            <span className="mut">{settled}</span>
          </span>
        );
      },
    },
  ];
}

const ITEM_COLUMNS: readonly DataTableColumn<OutboundQueueItem>[] = [
  {
    id: 'contract',
    header: 'Contract',
    cell: (i) => <Link to={`/contracts/record/${encodeURIComponent(i.contract_key)}`}>{i.contract_key}</Link>,
  },
  { id: 'target', header: 'Target', cell: (i) => i.target_system },
  { id: 'reftype', header: 'Ref type', cell: (i) => i.target_ref_type },
  { id: 'refid', header: 'Ref id', cell: (i) => i.target_ref_id },
  { id: 'status', header: 'Status', cell: (i) => i.queue_status },
  { id: 'attempts', header: 'Attempts', cell: (i) => i.attempt_count, cellClassName: 'num' },
  { id: 'last', header: 'Last attempt', cell: (i) => i.last_attempt_utc ?? '—' },
  { id: 'next', header: 'Next retry', cell: (i) => i.next_retry_utc ?? '—' },
  {
    id: 'error',
    header: 'Last error',
    cell: (i) =>
      i.last_error === null ? (
        '—'
      ) : (
        <code className="qerr" title={i.last_error}>
          {i.last_error}
        </code>
      ),
  },
];

export function OutboundHealthPanel({ windowDays }: { readonly windowDays: number }) {
  const [status, setStatus] = useState<StatusChoice>('both');
  const paging = usePaging();
  const { state, reload } = usePanel(
    (abort) =>
      fetchOutboundHealth(
        {
          window_days: windowDays,
          ...(status === 'both' ? {} : { status }),
          page: paging.cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor: paging.cursor },
        },
        abort,
      ),
    [windowDays, status, paging.cursor],
  );

  const pageMax =
    state.kind === 'ready' ? state.response.days.reduce((m, d) => Math.max(m, d.sent + d.failed + d.abandoned), 0) : 0;
  const queueEmpty =
    state.kind === 'ready' && state.response.cells.length === 0 && state.response.days.length === 0 && state.response.items.length === 0;

  return (
    <section className="card qpanel" aria-labelledby="q-outbound">
      <h3 id="q-outbound">{PANEL}</h3>
      <p className="mut">
        Queue depth by target and status is a fact about the system and is not filtered by your scope; the
        per-day series and the row list are.
      </p>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={FORBIDDEN_COPY} />
      ) : null}

      {state.kind === 'ready' && queueEmpty ? (
        <p>
          <strong>Nothing has been queued for outbound yet.</strong> Teams reminders (CLM-28) and the Qfly/XM writers
          (CLM-38) will appear here as they run.
        </p>
      ) : null}

      {state.kind === 'ready' && !queueEmpty ? (
        <>
          <div className="totals">
            <Tile
              label="Retry backlog"
              value={state.response.totals.retry_backlog}
              tone={state.response.totals.retry_backlog > 0 ? 'warn' : undefined}
            />
            <Tile
              label="Abandoned"
              value={state.response.totals.abandoned_total}
              tone={state.response.totals.abandoned_total > 0 ? 'bad' : undefined}
            />
            <Tile label="Oldest pending" value={state.response.totals.oldest_pending_utc ?? '—'} />
          </div>

          <DataTable
            caption="Queue by target and status"
            captionVisible
            columns={CELL_COLUMNS}
            rows={state.response.cells}
            rowKey={(c) => `${c.target_system}|${c.queue_status}`}
            empty={<p>No queue rows.</p>}
          />

          <DataTable
            caption="Settled per day"
            captionVisible
            columns={dayColumns(pageMax)}
            rows={state.response.days}
            rowKey={(d) => `${d.day}|${d.target_system}`}
            empty={<p>Nothing you can see settled in the last {state.response.totals.window_days} days.</p>}
          />

          <label className="check">
            Status
            <select
              aria-label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as StatusChoice);
                paging.reset();
              }}
            >
              <option value="both">Failed and Abandoned</option>
              <option value="Failed">Failed</option>
              <option value="Abandoned">Abandoned</option>
            </select>
          </label>

          <DataTable
            caption="Failed and abandoned writes you can see"
            captionVisible
            columns={ITEM_COLUMNS}
            rows={state.response.items}
            rowKey={(i) => i.outbound_id}
            empty={<p>No failed or abandoned writes on contracts you can see, in the window.</p>}
          />
          <CursorPager
            next_cursor={state.response.page.next_cursor}
            historyDepth={paging.historyDepth}
            onNext={() => {
              if (state.response.page.next_cursor !== null) paging.next(state.response.page.next_cursor);
            }}
            onPrevious={paging.previous}
            pageNumber={paging.page}
            returnedCount={state.response.page.returned_count}
            totalInScope={null}
          />
        </>
      ) : null}
    </section>
  );
}
